/** Normalize UPC/EAN digits for deduplication */
export function digitsOnly(v) {
  return String(v || '').replace(/\D+/g, '');
}

function isAllZerosDigits(d) {
  return !!d && /^0+$/.test(d);
}

/** Stored Label ID when the spec explicitly has no PLU (mutually exclusive with real labels). */
export const NO_PLU_LABEL = '__NO_PLU__';

export const NO_PLU_DISPLAY = 'No PLU';

function normalizedApiOption(raw) {
  const sourceType = String(raw?.sourceType || '').toUpperCase();
  const sourceId = Number(raw?.sourceId);
  const legacyValue = String(raw?.legacyValue || '').trim();
  const value = String(raw?.value || '').trim()
    || (sourceType === 'PID_LABEL' && Number.isInteger(sourceId) ? `pidlabel:${sourceId}` : '')
    || (sourceType === 'PIDUPC' && Number.isInteger(sourceId) ? `pidupc:${sourceId}` : '')
    || legacyValue;
  const digits = digitsOnly(raw?.upc ?? raw?.digits ?? raw?.label);
  if (!value || !digits || isAllZerosDigits(digits)) return null;
  return {
    value,
    legacyValue,
    sourceType,
    sourceId,
    label: digits,
    digits,
  };
}

export function normalizeUpcOptionPayload(data) {
  if (Array.isArray(data)) {
    const options = data.map(normalizedApiOption).filter(Boolean);
    return { options, refs: options };
  }
  const options = (data?.options || []).map(normalizedApiOption).filter(Boolean);
  const refsRaw = Array.isArray(data?.refs) ? data.refs : data?.options;
  const refs = (refsRaw || []).map(normalizedApiOption).filter(Boolean);
  return { options, refs: refs.length ? refs : options };
}

/**
 * Build merged UPC picker options from PIDUPC rows and PIDBags rows.
 * When the same digit string exists on PIDUPC, that row wins; otherwise each bag row is listed (incl. duplicate digits across bags).
 */
export function buildMergedUpcOptions(pidUpcRows, pidBagRows) {
  const chosenDigits = new Set();
  const options = [];

  // PIDUPC wins whenever a digit string exists in both sources.
  // Also de-duplicate within PIDUPC by digits.
  for (const u of pidUpcRows || []) {
    const d = digitsOnly(u.UPC);
    if (!d || isAllZerosDigits(d) || chosenDigits.has(d)) continue;
    const id = Number(u.UniqueID);
    if (!Number.isFinite(id)) continue;
    const label = d;
    chosenDigits.add(d);
    options.push({ value: `upc:${id}`, kind: 'pidupc', pidUpcId: id, pidBagId: null, label, digits: d });
  }

  // Include PIDBag UPCs only for digit strings not already present on PIDUPC.
  // Also de-duplicate within PIDBag by digits.
  for (const b of pidBagRows || []) {
    const d = digitsOnly(b.UPC);
    if (!d || isAllZerosDigits(d) || chosenDigits.has(d)) continue;
    const bid = Number(b.UniqueID);
    if (!Number.isFinite(bid)) continue;
    const label = d;
    chosenDigits.add(d);
    options.push({ value: `bag:${bid}`, kind: 'pidbag', pidUpcId: null, pidBagId: bid, label, digits: d });
  }
  options.sort((a, b) => a.digits.localeCompare(b.digits) || a.label.localeCompare(b.label));
  return options;
}

export function buildUpcOptionLookup(pidUpcRows, pidBagRows, mergedOptions = []) {
  const map = new Map();
  for (const o of mergedOptions || []) {
    map.set(String(o.value), o);
  }

  for (const u of pidUpcRows || []) {
    const d = digitsOnly(u.UPC);
    const id = Number(u.UniqueID);
    if (!d || isAllZerosDigits(d) || !Number.isFinite(id)) continue;
    const value = `upc:${id}`;
    if (!map.has(value)) {
      map.set(value, { value, kind: 'pidupc', pidUpcId: id, pidBagId: null, label: d, digits: d });
    }
  }

  for (const b of pidBagRows || []) {
    const d = digitsOnly(b.UPC);
    const id = Number(b.UniqueID);
    if (!d || isAllZerosDigits(d) || !Number.isFinite(id)) continue;
    const value = `bag:${id}`;
    if (!map.has(value)) {
      map.set(value, { value, kind: 'pidbag', pidUpcId: null, pidBagId: id, label: d, digits: d });
    }
  }

  return map;
}

export function upcSelectionsToPayload(selectedValues) {
  const upcIds = [];
  const bagIds = [];
  const refs = [];
  for (const raw of selectedValues || []) {
    const s = String(raw || '');
    if (s.startsWith('upc:') || s.startsWith('pidupc:')) {
      const n = Number(s.split(':')[1]);
      if (Number.isInteger(n)) {
        upcIds.push(n);
        refs.push({ sourceType: 'PIDUPC', sourceId: n, value: `pidupc:${n}` });
      }
    } else if (s.startsWith('bag:') || s.startsWith('pidlabel:')) {
      const n = Number(s.split(':')[1]);
      if (Number.isInteger(n)) {
        bagIds.push(n);
        refs.push({ sourceType: 'PID_LABEL', sourceId: n, value: `pidlabel:${n}` });
      }
    }
  }
  return { UPCIDs: upcIds, PIDBagIDs: bagIds, UPCRefs: refs };
}

export function apiUpcRefsToSelections(upcIds, pidBagIds) {
  if (Array.isArray(upcIds) && upcIds.some((ref) => typeof ref === 'object' && ref !== null)) {
    return upcIds
      .map((ref) => {
        const value = String(ref.value || '').trim();
        if (value) return value;
        const sourceType = String(ref.sourceType || ref.source || '').toUpperCase();
        const id = Number(ref.sourceId ?? ref.id);
        if (!Number.isInteger(id)) return '';
        return sourceType === 'PID_LABEL' ? `pidlabel:${id}` : `pidupc:${id}`;
      })
      .filter(Boolean);
  }
  const out = [];
  for (const id of upcIds || []) {
    const n = Number(id);
    if (Number.isInteger(n)) out.push(`pidupc:${n}`);
  }
  for (const id of pidBagIds || []) {
    const n = Number(id);
    if (Number.isInteger(n)) out.push(`pidlabel:${n}`);
  }
  return out;
}

export function labelForUpcSelection(value, optionByValue) {
  const o = optionByValue.get(String(value));
  return o ? o.label : String(value);
}
